import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPinLine, MagnifyingGlass, FloppyDisk, House } from "@phosphor-icons/react";

/**
 * DepotSettingsPanel — admin editor for the payroll reference depot.
 * Two ways to fill lat/lng :
 *   1. Type an address → click "Rechercher" → pick a Nominatim result (auto-fill).
 *   2. Paste coords directly (fallback when Nominatim rate-limits 429).
 */
export default function DepotSettingsPanel() {
  const [depot, setDepot] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/depot");
        setDepot(r.data);
        setName(r.data.name || "");
        setAddress(r.data.address || "");
        setLatitude(r.data.latitude?.toString() ?? "");
        setLongitude(r.data.longitude?.toString() ?? "");
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, []);

  const searchAddress = async () => {
    const q = address.trim();
    if (q.length < 3) {
      toast.error("Saisis au moins 3 caractères");
      return;
    }
    setGeocoding(true);
    try {
      const r = await api.get(`/geocode?q=${encodeURIComponent(q)}`);
      const results = r.data.results || [];
      if (results.length === 0) {
        toast.error("Aucun résultat — vérifie l'adresse ou saisis les coords manuellement");
      } else {
        setSuggestions(results);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGeocoding(false);
    }
  };

  const pickSuggestion = (s) => {
    setLatitude(s.latitude.toString());
    setLongitude(s.longitude.toString());
    setAddress(s.label);
    setSuggestions([]);
    toast.success("Coordonnées mises à jour");
  };

  const save = async () => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      toast.error("Latitude / longitude invalides");
      return;
    }
    if (!name.trim()) {
      toast.error("Nom du dépôt requis");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/depot", { name: name.trim(), address: address.trim() || null, latitude: lat, longitude: lon });
      setDepot(r.data);
      toast.success("Dépôt mis à jour · payroll recalcule immédiatement");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="depot-settings-panel">
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/15 to-[#005CFF]/5 border border-[#005CFF]/25 p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)] shrink-0">
          <MapPinLine size={22} weight="fill" color="white" />
        </div>
        <div className="flex-1 text-sm text-white/85 leading-relaxed">
          <b className="text-white">Dépôt de référence pour la paie</b>
          <div className="text-xs text-white/60 mt-1">
            Les kilomètres vol d&apos;oiseau sur <b>/heures</b> sont calculés depuis ces coordonnées vers chaque chantier. Un tech avec une adresse domicile renseignée utilisera son domicile en priorité.
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <Field label="Nom du dépôt *">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Dépôt Saint-Aunès" data-testid="depot-name" />
        </Field>

        <Field label="Adresse (recherche automatique)">
          <div className="flex gap-2">
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="12 rue de la Justice, 34130 Saint-Aunès" data-testid="depot-address" onKeyDown={(e) => e.key === "Enter" && searchAddress()} />
            <Button onClick={searchAddress} disabled={geocoding} variant="ghost" className="border border-[#005CFF]/40 text-[#7CB2FF] hover:bg-[#005CFF]/10" data-testid="depot-geocode">
              <MagnifyingGlass size={13} className={geocoding ? "animate-spin" : ""} />
            </Button>
          </div>
        </Field>

        {suggestions.length > 0 && (
          <div className="rounded-xl bg-black/40 border border-white/10 divide-y divide-white/5" data-testid="depot-suggestions">
            {suggestions.map((s, i) => (
              <button key={`${s.latitude}-${s.longitude}-${i}`} onClick={() => pickSuggestion(s)} className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-center gap-2 text-xs">
                <MapPinLine size={12} className="text-[#7CB2FF] shrink-0" weight="fill" />
                <div className="flex-1 truncate">{s.label}</div>
                <div className="text-white/40 font-mono text-[10px]">{s.latitude.toFixed(4)}, {s.longitude.toFixed(4)}</div>
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Field label="Latitude *">
            <Input type="number" step="0.0001" value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="43.5749" data-testid="depot-lat" />
          </Field>
          <Field label="Longitude *">
            <Input type="number" step="0.0001" value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="3.9700" data-testid="depot-lng" />
          </Field>
        </div>

        <div className="flex items-center justify-between pt-2">
          {depot?.updated_at ? (
            <div className="text-[10px] text-white/40">
              Mis à jour {new Date(depot.updated_at).toLocaleString("fr-FR")} par {depot.updated_by_name || "—"}
            </div>
          ) : <div />}
          <Button onClick={save} disabled={saving} className="bg-[#005CFF] hover:bg-[#0047CC] gap-1.5" data-testid="depot-save">
            <FloppyDisk size={13} weight="fill" /> Enregistrer
          </Button>
        </div>
      </div>

      {latitude && longitude && (
        <div className="rounded-2xl bg-white/[0.02] border border-white/10 overflow-hidden">
          <div className="px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
            <House size={11} weight="fill" /> Aperçu carte
          </div>
          <iframe
            title="Carte dépôt"
            className="w-full h-64"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${(parseFloat(longitude) - 0.02).toFixed(4)},${(parseFloat(latitude) - 0.01).toFixed(4)},${(parseFloat(longitude) + 0.02).toFixed(4)},${(parseFloat(latitude) + 0.01).toFixed(4)}&layer=mapnik&marker=${latitude},${longitude}`}
            style={{ filter: "invert(0.92) hue-rotate(180deg)" }}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</div>
      {children}
    </label>
  );
}
